import React from "react";
import dayjs from "dayjs";
import ModalImage from "react-modal-image";
import useUniversalStore from "store/universalStore";
import useOffcanvasStore from "store/offcanvasStore";
import { transactionAPI, accountAPI } from "services";
import { formatMoney } from "utils";
import { confirm, TxnActions } from "components";

const TxnsTableView = ({
  txns = [],
  openBalance = 0,
  handleToggle,
  setLoading,
  loadTxns,
  selectedMonth = new Date(),
  isSharedAccount = false,
}) => {
  const { write } = useUniversalStore((state) => state);
  const { openOffcanvas } = useOffcanvasStore();

  const handleModalOpen = (txnId) => {
    confirm({
      message: "You want to delete this <b>Transaction</b>",
      onConfirm: () => deleteTransaction(txnId),
    });
  };

  const deleteTransaction = async (id) => {
    setLoading(true);
    try {
      await transactionAPI.deleteTransaction(id);
      loadTxns?.();
      write({
        transactionId: null,
        accounts: await accountAPI.getAccounts(),
      });
      handleToggle?.(false);
    } catch (err) {
      console.error("deleteTransaction error in table view:", err);
    }
  };

  const editTransaction = (txnId) => {
    openOffcanvas("editTxn");
    write({ transactionId: txnId });
  };

  const renderAttachment = (attachment) => {
    if (!attachment) {
      return <span className="text-muted"></span>;
    }

    const isPDF = attachment?.includes(".pdf");
    const isImage = attachment?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

    if (isPDF) {
      return (
        <a
          role="button"
          href={attachment}
          target="_blank"
          rel="noreferrer"
          className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 py-1 px-2 rounded"
          title="Open PDF Attachment"
        >
          <i className="bi bi-file-pdf-fill fs-6" />
          <span className="d-none d-lg-inline fs-xmd-smaller">PDF</span>
        </a>
      );
    }

    if (isImage) {
      return (
        <div className="d-inline-flex align-items-center justify-content-center" title="Click to view attachment">
          <ModalImage
            small={attachment}
            large={attachment}
            hideDownload={false}
            hideZoom={false}
            alt="Attachment"
            className="table-attachment-img rounded border shadow-sm cursor-pointer"
          />
        </div>
      );
    }

    return (
      <a
        role="button"
        href={attachment}
        target="_blank"
        rel="noreferrer"
        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 py-1 px-2 rounded"
        title="Open Attachment"
      >
        <i className="bi bi-paperclip fs-6" />
        <span className="d-none d-lg-inline fs-xmd-smaller">View</span>
      </a>
    );
  };

  return (
    <div className="w-100 p-0 m-0">
      <div
        className="table-responsive scrollbar-design w-100 pb-5"
        style={{
          height: "calc(100vh - 225px)",
          maxHeight: "calc(100vh - 225px)",
          overflowY: "auto",
        }}
      >
        <table className="table table-hover align-middle mb-0 khata-statement-table">
          <thead className="table-light position-sticky top-0 z-1">
            <tr>
              <th
                scope="col"
                className="text-start py-3 px-3 text-secondary text-nowrap"
                style={{ width: "140px" }}
              >
                Txn Date
              </th>
              <th scope="col" className="text-start py-3 px-2 text-secondary" style={{ minWidth: "300px" }}>
                Description
              </th>
              <th
                scope="col"
                className="text-end py-3 px-3 text-secondary text-nowrap"
                style={{ width: "140px" }}
              >
                Amount Received
              </th>
              <th
                scope="col"
                className="text-end py-3 px-3 text-secondary text-nowrap"
                style={{ width: "140px" }}
              >
                Amount Given
              </th>
              <th
                scope="col"
                className="text-end py-3 px-2 text-secondary text-nowrap"
                style={{ width: "140px" }}
              >
                Balance
              </th>
              {!isSharedAccount && (
                <th
                  scope="col"
                  className="text-center py-3 px-2 text-secondary"
                  style={{ width: "50px" }}
                />
              )}
            </tr>
          </thead>
          <tbody>
            {/* Transactions List */}
            {txns.length > 0 && (
              txns.map((txn, index) => {
                const isReceived = txn.amount > 0;
                const isGiven = txn.amount < 0;
                const isCancelled = txn.cancelled;

                return (
                  <tr
                    key={txn.id || index}
                    className={`border-bottom ${isCancelled ? "opacity-75 bg-light-subtle" : ""}`}
                  >
                    {/* Txn Date */}
                    <td className="text-start py-2 px-3 text-nowrap">
                      <span
                        className={`fs-xmd-smaller ${isCancelled
                          ? "text-muted text-decoration-line-through"
                          : "text-dark"
                          }`}
                      >
                        {dayjs(txn.date).format("DD MMM YYYY")}
                      </span>
                    </td>

                    {/* Description */}
                    <td className="text-start py-2 px-2">
                      <div className="d-flex align-items-center flex-wrap">
                        <span
                          className={`text-break me-2 ${isCancelled
                            ? "text-muted text-decoration-line-through"
                            : "text-dark"
                            }`}
                        >
                          {txn.detail || "-"}
                        </span>
                        {renderAttachment(txn.attachment)}
                        {txn.type === "CREDIT" && (
                          <span
                            className="badge border border-danger text-danger ms-2"
                            style={{ fontSize: "10px" }}
                          >
                            CREDIT
                          </span>
                        )}
                        {isCancelled && (
                          <span
                            className="badge bg-secondary ms-2"
                            style={{ fontSize: "10px" }}
                          >
                            Cancelled
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Amount Received */}
                    <td className="text-end py-2 px-3 text-nowrap">
                      {isReceived ? (
                        <span
                          className={`fw-semibold ${isCancelled
                            ? "text-muted text-decoration-line-through"
                            : "text-muted"
                            }`}
                        >
                          {formatMoney(txn.amount)}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    {/* Amount Given */}
                    <td className="text-end py-2 px-3 text-nowrap">
                      {isGiven ? (
                        <span
                          className={`fw-semibold ${isCancelled
                            ? "text-muted text-decoration-line-through"
                            : "text-muted"
                            }`}
                        >
                          {formatMoney(Math.abs(txn.amount))}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    {/* Balance */}
                    <td className="text-end py-2 px-2 text-nowrap">
                      <span
                        className={`fw-semibold ${isCancelled
                          ? "text-muted text-decoration-line-through"
                          : "text-muted"
                          }`}
                      >
                        {formatMoney(txn.balance)}
                      </span>
                    </td>

                    {/* Actions Dropdown */}
                    {!isSharedAccount && (
                      <td className="text-center py-2 px-2 text-nowrap">
                        {!isCancelled ? (
                          <TxnActions
                            onEdit={() => editTransaction(txn.id)}
                            onDelete={() => handleModalOpen(txn.id)}
                          />
                        ) : (
                          <span className="text-muted"></span>
                        )}
                      </td>
                    )}

                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TxnsTableView;
